import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Typography,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningIcon from '@mui/icons-material/Warning';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PendingIcon from '@mui/icons-material/Pending';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import ProductionPlanAddDialog from './ProductionPlanAddDialog';

// API
import * as manufacturingApi from '../../../lib/api/manufacturing';

/**
 * 생산 계획 목록 컴포넌트
 * - API 데이터 연동
 * - 워크플로우 기반 라우팅 자동 생성
 * - 공정 상태 변경 (시작/완료) → QC 자동 생성 트리거
 */
const ProductionPlanList = ({ productionPlan, planData, projectInfo, onRefresh }) => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // 라우팅 데이터 (워크플로우 진행 현황)
  const [routingData, setRoutingData] = useState(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  
  // 공정 상태 변경 확인 다이얼로그
  // 종결 확인 다이얼로그
  const [closeConfirmDialog, setCloseConfirmDialog] = useState(false);
  
  // 삭제 확인 다이얼로그
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);

  // API 데이터가 있으면 사용
  const hasPlanData = planData && planData.planId;
  
  // 워크플로우 잠금 여부 (완료된 공정이 1개라도 있으면 잠금)
  const isWorkflowLocked = routingData?.details?.some(
    detail => detail.processStatus === 'COMPLETED' || detail.processStatus === 'IN_PROGRESS'
  ) || false;

  // 라우팅 데이터 조회
  const fetchRoutingData = useCallback(async () => {
    if (!planData?.planId) return;
    
    setRoutingLoading(true);
    try {
      const response = await manufacturingApi.getRoutingsByPlan(planData.planId);
      console.log('라우팅 데이터:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setRoutingData(response.data.data[0]);
      } else {
        setRoutingData(null);
      }
    } catch (err) {
      console.error('라우팅 데이터 조회 실패:', err);
      setRoutingData(null);
    } finally {
      setRoutingLoading(false);
    }
  }, [planData?.planId]);

  useEffect(() => {
    if (hasPlanData) {
      fetchRoutingData();
    }
  }, [hasPlanData, fetchRoutingData]);

  // 예정일 상태 계산 함수
  const getDeadlineStatus = (expectedDate, status) => {
    if (status === 'COMPLETED' || status === '완료') {
      return 'completed';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const deadline = new Date(expectedDate);
    deadline.setHours(0, 0, 0, 0);
    
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'overdue';
    } else if (diffDays <= 3) {
      return 'warning';
    } else {
      return 'normal';
    }
  };

  // 상태별 색상
  const getStatusColor = (status) => {
    switch (status) {
      case 'PLAN':
      case 'DRAFT':
      case 'PENDING':
      case '대기':
        return 'default';
      case 'IN_PROGRESS':
      case '진행중':
        return 'warning';
      case 'COMPLETED':
      case '완료':
        return 'success';
      case 'CANCELLED':
      case 'SKIPPED':
      case '취소':
        return 'error';
      case 'CLOSED':
      case '종결':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // 상태 라벨
  const getStatusLabel = (status) => {
    // 공통코드(MANU/PLAN_STATUS)에 있는 값은 여기에도 있어야 한다. 빠지면 코드가 그대로
    // 화면에 나온다.
    const statusMap = {
      'PLAN': '대기',
      'DRAFT': '대기',
      'PENDING': '대기',
      'IN_DESIGN': '설계중',
      'IN_PRODUCTION': '생산중',
      'IN_PROGRESS': '진행중',
      'IN_QC': 'QC검사중',
      'PRODUCED': '생산완료',
      'QC_FAILED': '품질불합격',
      'SHIPPED': '출하완료',
      'COMPLETED': '완료',
      'CANCELLED': '취소',
      'CLOSED': '종결',
      'SKIPPED': '건너뜀',
    };
    return statusMap[status] || status || '대기';
  };

  // 공정 상태 아이콘
  // 공정 카드에 붙는 QC 배지. 완품검사 공정이면 판정 여부를, 공정검사를 실시한 공정이면
  // 생산/불량 수량을 보여준다. 둘 다 아니면 아무 것도 그리지 않는다(검사 없이 지나간 공정).
  const renderQcBadge = (detail) => {
    if (detail.processMstTyp === 'INSPECTION') {
      const judged = detail.processStatus === 'COMPLETED';
      return (
        <Chip
          label={judged ? '완품검사 판정완료' : '완품검사 대기'}
          size="small"
          color={judged ? 'success' : 'warning'}
          variant="outlined"
          sx={{ mb: 1, height: 22 }}
        />
      );
    }
    if (detail.qcYn === 'Y') {
      const out = Number(detail.outputQty) || 0;
      const def = Number(detail.defectQty) || 0;
      return (
        <Chip
          label={`공정검사 ${out} / 불량 ${def}`}
          size="small"
          color={def > 0 ? 'warning' : 'info'}
          variant="outlined"
          sx={{ mb: 1, height: 22 }}
        />
      );
    }
    // 검사 대상으로 지정됐지만(qcTargetYn='Y') 아직 실적이 안 들어온 공정.
    // 검사 대상이 아닌 공정과 섞이면 구분이 안 된다.
    if (detail.qcTargetYn === 'Y') {
      return (
        <Chip
          label="검사 대기"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ mb: 1, height: 22 }}
        />
      );
    }
    return null;
  };

  const getProcessStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 32 }} />;
      case 'IN_PROGRESS':
        return <PlayArrowIcon sx={{ color: '#ff9800', fontSize: 32 }} />;
      case 'SKIPPED':
        return <SkipNextIcon sx={{ color: '#9e9e9e', fontSize: 32 }} />;
      default:
        return <PendingIcon sx={{ color: '#bdbdbd', fontSize: 32 }} />;
    }
  };

  // 이 카드는 조회 전용이다. 공정 시작/완료는 생산 탭에서만 한다.
  // 예전에는 여기에도 완료 처리가 있었는데, 그 경로는 공정검사(qcYn/생산수량/불량수량)를
  // 받지 않아서 어디서 완료했느냐에 따라 공정 QC 가 조용히 누락됐다. 되살리려면 생산 탭과
  // 같은 완료 다이얼로그를 태워야 한다.

  const handleAddNew = () => {
    setSelectedPlan(null);
    setOpenAddDialog(true);
  };

  const handleEdit = () => {
    console.log('=== handleEdit: planData ===', planData);
    setSelectedPlan({
      ...planData,
      planCode: planData.planNumber,
      // 제품 정보
      productUuid: planData.productId || planData.productUuid,
      productName: planData.productName,
      productSpec: planData.productSpec || '',
      planQty: planData.planQty,
      // 워크플로우
      workflowUuid: planData.workflowUuid,
      // 예정일 (planEndDate -> expectedCompletionDate)
      planStartDate: planData.planStartDate || '',
      expectedCompletionDate: planData.planEndDate || planData.expectedCompletionDate,
      // 담당자 UUID
      designerUuid: planData.designManagerUuid || planData.designerUuid,
      productionManagerUuid: planData.productionManagerUuid,
      qcManagerUuid: planData.qcManagerUuid,
      // 담당자명
      designerName: planData.designManagerName || planData.designerName,
      productionManagerName: planData.productionManagerName,
      qcManagerName: planData.qcManagerName,
      // 기타
      remarks: planData.remarks,
    });
    setOpenAddDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setSelectedPlan(null);
    setError(null);
  };
  
  // 생산계획 종결 핸들러
  const handleClosePlan = () => {
    setCloseConfirmDialog(true);
  };
  
  // 생산계획 종결 확정
  const confirmClosePlan = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await manufacturingApi.updateManufacturingPlanStatus(planData.planId, {
        status: 'CLOSED',
      });
      
      if (response.data.success) {
        setSuccessMessage('생산계획이 종결되었습니다.');
        setCloseConfirmDialog(false);
        
        // 부모 컴포넌트 새로고침
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(response.data.message || '종결 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('생산계획 종결 실패:', err);
      setError(err.response?.data?.message || err.message || '종결 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 생산계획 삭제 핸들러
  const handleDeletePlan = () => {
    setDeleteConfirmDialog(true);
  };
  
  // 생산계획 삭제 확정
  const confirmDeletePlan = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await manufacturingApi.deleteManufacturingPlan(planData.planId);
      
      if (response.data.success) {
        setSuccessMessage('생산계획이 삭제되었습니다.');
        setDeleteConfirmDialog(false);
        
        // 목록으로 이동 (또는 새로고침)
        if (onRefresh) {
          onRefresh();
        }
        // 목록 페이지로 돌아가기
        window.history.back();
      } else {
        throw new Error(response.data.message || '삭제 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('생산계획 삭제 실패:', err);
      setError(err.response?.data?.message || err.message || '삭제 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 생산계획 저장 + 라우팅 자동 생성
  const handleSave = async (formData) => {
    console.log('=== handleSave 시작 ===');
    console.log('formData:', formData);
    console.log('selectedPlan:', selectedPlan);
    console.log('planData:', planData);
    
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      let savedPlanId = planData?.planId;
      
      // 1. 생산계획 저장/수정
      if (selectedPlan) {
        // 수정 모드
        const updateData = {
          productId: formData.productUuid,
          productName: formData.productName,
          // 시작일을 안 보내고 있었다. 그래서 한 번 만들면 고칠 방법이 없었다.
          planStartDate: formData.planStartDate,
          planEndDate: formData.expectedCompletionDate,
          designManagerUuid: formData.designerUuid,
          designManagerName: formData.designerName,
          productionManagerUuid: formData.productionManagerUuid,
          productionManagerName: formData.productionManagerName,
          qcManagerUuid: formData.qcManagerUuid,
          qcManagerName: formData.qcManagerName,
          planQty: formData.planQty ? parseInt(formData.planQty, 10) : null,
          remarks: formData.remarks,
          workflowUuid: formData.workflowUuid,
        };
        
        console.log('=== 생산계획 수정 API 호출 ===');
        console.log('planId:', savedPlanId);
        console.log('updateData:', updateData);
        
        const updateResponse = await manufacturingApi.updateManufacturingPlan(savedPlanId, updateData);
        console.log('생산계획 수정 응답:', updateResponse.data);
        
        if (!updateResponse.data.success) {
          throw new Error(updateResponse.data.message || '생산계획 수정에 실패했습니다.');
        }
        
        setSuccessMessage('생산계획이 수정되었습니다.');
        
      } else {
        // 신규 생성 모드
        console.log('=== formData 확인 ===');
        console.log('formData:', formData);
        console.log('formData.productUuid:', formData.productUuid);
        console.log('formData.designerUuid:', formData.designerUuid);
        console.log('formData.productionManagerUuid:', formData.productionManagerUuid);
        console.log('formData.qcManagerUuid:', formData.qcManagerUuid);
        
        const createData = {
          projectId: projectInfo?.projectId || projectInfo?.cstInfUuid,
          // 제품 정보
          productId: formData.productUuid || null,
          productName: formData.productName || null,
          // 수량/일정
          planQty: formData.planQty ? parseInt(formData.planQty, 10) : null,
          planStartDate: formData.planStartDate || new Date().toISOString().split('T')[0],
          planEndDate: formData.expectedCompletionDate,
          // 담당자 정보 - 필드명 명확하게 매핑
          designManagerUuid: formData.designerUuid || null,
          designManagerName: formData.designerName || null,
          productionManagerUuid: formData.productionManagerUuid || null,
          productionManagerName: formData.productionManagerName || null,
          qcManagerUuid: formData.qcManagerUuid || null,
          qcManagerName: formData.qcManagerName || null,
          // 기타
          remarks: formData.remarks || null,
          workflowUuid: formData.workflowUuid || null,
        };
        
        console.log('=== 생산계획 생성 API 호출 ===');
        console.log('createData:', createData);
        console.log('createData.designManagerUuid:', createData.designManagerUuid);
        console.log('createData.productId:', createData.productId);
        
        const response = await manufacturingApi.createManufacturingPlan(createData);
        console.log('생산계획 생성 응답:', response.data);
        
        if (!response.data.success) {
          throw new Error(response.data.message || '생산계획 생성에 실패했습니다.');
        }
        
        if (response.data.data) {
          savedPlanId = response.data.data.planId || response.data.data.planUuid;
        }
        
        setSuccessMessage('생산계획이 생성되었습니다.');
      }
      
      // 2. 워크플로우 기반 라우팅 생성
      // - 신규 생성 시: 라우팅 생성
      // - 수정 시: 워크플로우가 변경된 경우에만 라우팅 재생성
      if (formData.workflowUuid && savedPlanId) {
        const isNewPlan = !selectedPlan;
        const isWorkflowChanged = selectedPlan && selectedPlan.workflowUuid !== formData.workflowUuid;
        const hasNoRouting = !routingData;
        
        // 신규 생성이거나, 라우팅이 없거나, 워크플로우가 변경된 경우에만 라우팅 생성
        if (isNewPlan || hasNoRouting || isWorkflowChanged) {
          try {
            console.log('=== 라우팅 생성 API 호출 ===');
            console.log('isNewPlan:', isNewPlan, 'hasNoRouting:', hasNoRouting, 'isWorkflowChanged:', isWorkflowChanged);
            
            const routingData = {
              planId: savedPlanId,
              workflowUuid: formData.workflowUuid,
              productUuid: formData.productUuid,
            };
            console.log('routingData:', routingData);
            
            const routingResponse = await manufacturingApi.createRoutingFromWorkflow(routingData);
            console.log('라우팅 생성 응답:', routingResponse.data);
            
            if (routingResponse.data.success) {
              if (isWorkflowChanged) {
                setSuccessMessage(prev => (prev || '') + ' 워크플로우가 변경되어 라우팅이 재생성되었습니다.');
              } else {
                setSuccessMessage(prev => (prev || '') + ' 워크플로우 라우팅도 생성되었습니다.');
              }
            }
          } catch (routingError) {
            console.error('라우팅 생성 실패:', routingError);
            console.error('라우팅 생성 에러 응답:', routingError.response?.data);
            setError(
              '생산계획은 저장되었으나, 워크플로우 라우팅 생성에 실패했습니다. 생산계획을 수정하여 워크플로우를 다시 선택해주세요. '
                + (routingError.response?.data?.message || routingError.message || '')
            );
          }
        } else {
          console.log('=== 라우팅 생성 생략 (기존 라우팅 유지) ===');
        }
      }
      
      // 성공 시 다이얼로그 닫기
      handleCloseDialog();
      
      // 부모 컴포넌트 새로고침
      if (onRefresh) {
        onRefresh();
      }
      
      // 라우팅 데이터 새로고침
      fetchRoutingData();
      
    } catch (err) {
      console.error('=== 저장 실패 ===');
      console.error('에러 객체:', err);
      console.error('에러 응답:', err.response);
      console.error('에러 응답 데이터:', err.response?.data);
      console.error('에러 메시지:', err.message);
      
      // 에러 메시지 추출
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || '저장에 실패했습니다.';
      
      // 에러 표시 (Snackbar로 화면 상단에 표시)
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // 데이터가 없는 경우
  if (!hasPlanData) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          생산 계획 정보가 없습니다
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          sx={{ mt: 2 }}
        >
          생산 계획 추가
        </Button>
        
        <ProductionPlanAddDialog
          open={openAddDialog}
          onClose={handleCloseDialog}
          onSave={handleSave}
          selectedPlan={null}
        />
      </Box>
    );
  }

  const deadlineStatus = getDeadlineStatus(planData.planEndDate, planData.status);

  return (
    <Box>
      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* 성공 메시지 */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* 생산 계획 요약 카드 */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              생산 계획 상세
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={getStatusLabel(planData.status)} 
                color={getStatusColor(planData.status)}
                variant="filled"
              />
              {/* 종결 버튼 - 완료/종결 상태가 아닐 때 표시 */}
              {planData.status !== 'CLOSED' && planData.status !== 'COMPLETED' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleClosePlan}
                >
                  종결
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                disabled={planData.status === 'CLOSED' || planData.status === 'COMPLETED'}
              >
                수정
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDeletePlan}
                disabled={isWorkflowLocked}
              >
                삭제
              </Button>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            {/* 기본 정보 */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                기본 정보
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">생산계획번호</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {planData.planNumber}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">제품명</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {planData.productName || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">생산 수량</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {planData.planQty ? 
                      `${planData.planQty.toLocaleString()}개` : '-'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* 담당자 정보 */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                담당자 정보
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">설계 담당자</Typography>
                  <Typography variant="body2">
                    {planData.designManagerName || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">생산 담당자</Typography>
                  <Typography variant="body2">
                    {planData.productionManagerName || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">QC 담당자</Typography>
                  <Typography variant="body2">
                    {planData.qcManagerName || '-'}
                  </Typography>
                </Box>
                {/* 고른 워크플로우. 이게 안 보여서 "안 고른 것 같다" 는 이야기가 나왔다. */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">공정 워크플로우</Typography>
                  <Typography variant="body2">
                    {planData.workflowNm
                      || (planData.workflowUuid ? '이름을 찾을 수 없음' : '-')}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* 일정 정보 */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                일정 정보
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">시작일</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{planData.planStartDate}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">완료 예정일</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {deadlineStatus === 'overdue' && (
                      <Tooltip title="마감일 초과">
                        <EventBusyIcon sx={{ color: '#d32f2f', fontSize: 16 }} />
                      </Tooltip>
                    )}
                    {deadlineStatus === 'warning' && (
                      <Tooltip title="마감 임박">
                        <WarningIcon sx={{ color: '#ed6c02', fontSize: 16 }} />
                      </Tooltip>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: deadlineStatus === 'overdue' ? '#d32f2f' : 'text.primary',
                        fontWeight: deadlineStatus === 'overdue' ? 600 : 400,
                      }}
                    >
                      {planData.planEndDate}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* 진행률 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 60 }}>
                  {routingData?.progressRate || 0}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={routingData?.progressRate || 0}
                  sx={{
                    flex: 1,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background:
                        (routingData?.progressRate || 0) === 100
                          ? 'linear-gradient(90deg, #4CAF50, #8BC34A)'
                          : 'linear-gradient(90deg, #2196F3, #64B5F6)',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 워크플로우 진행 현황 - 조회 전용 */}
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              워크플로우 진행 현황
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {routingData && (
                <Chip 
                  label={`${routingData.completedSteps || 0}/${routingData.totalSteps || 0} 완료`}
                  color={routingData.routingStatus === 'COMPLETED' ? 'success' : 'primary'}
                  size="small"
                />
              )}
              <IconButton size="small" onClick={fetchRoutingData} disabled={routingLoading}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          {routingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : routingData && routingData.details && routingData.details.length > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
              {routingData.details.map((detail, index) => {
                const isInProgress = detail.processStatus === 'IN_PROGRESS';
                const isCompleted = detail.processStatus === 'COMPLETED';
                const isSkipped = detail.processStatus === 'SKIPPED';
                
                return (
                  <React.Fragment key={detail.routingDetUuid}>
                    {/* 공정 카드 */}
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        minWidth: 120,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 
                          isCompleted ? '#e8f5e9' :
                          isInProgress ? '#fff3e0' :
                          isSkipped ? '#f5f5f5' :
                          '#fafafa',
                        border: isInProgress ? '2px solid #ff9800' : '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        {getProcessStatusIcon(detail.processStatus)}
                      </Box>
                      
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {detail.processNm}
                      </Typography>
                      
                      <Chip 
                        label={getStatusLabel(detail.processStatus)} 
                        size="small" 
                        color={getStatusColor(detail.processStatus)}
                        sx={{ mb: 1, height: 22 }} 
                      />

                      {/* QC 구분 - 두 QC 는 성격도 결과도 다른데 카드에는 공정명과 상태만
                          나와서, 보고 있는 게 어느 쪽인지 화면으로 알 수가 없었다.
                          완품검사: 계획 전체를 합격/불합격으로 판정(불합격이면 전량 폐기)
                          공정검사: 그 공정에서 선택적으로 실시한 부분 검사(수량 기준) */}
                      {renderQcBadge(detail)}
                      
                      {/* 날짜 정보 */}
                      {(detail.actualStartDt || detail.actualEndDt) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {detail.actualStartDt && `시작: ${detail.actualStartDt}`}
                          {detail.actualEndDt && <br />}
                          {detail.actualEndDt && `완료: ${detail.actualEndDt}`}
                        </Typography>
                      )}
                      
                      {/* 액션 버튼 제거 - 조회 전용 (공정 시작/완료는 생산 탭에서 처리) */}
                    </Box>

                    {/* 화살표 */}
                    {index < routingData.details.length - 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                        <Typography variant="h5" color="text.disabled">→</Typography>
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                워크플로우가 설정되지 않았습니다.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                생산 계획을 수정하여 워크플로우를 선택해주세요.
              </Typography>
            </Box>
          )}

          {/* 라우팅 완료 시 다음 단계 안내 - QC 탭은 없어졌다. 완품 QC 는 검사 공정으로 들어갔고,
              여기까지 왔으면 판정도 끝난 상태라 남은 일은 출고다. */}
          {routingData?.routingStatus === 'COMPLETED' && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                🎉 모든 공정이 완료되었습니다.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                완료 탭에서 창고 입고 또는 즉시 출고를 진행하세요.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* 생산계획 추가/수정 다이얼로그 */}
      <ProductionPlanAddDialog
        open={openAddDialog}
        onClose={handleCloseDialog}
        onSave={handleSave}
        selectedPlan={selectedPlan}
        isWorkflowLocked={isWorkflowLocked}
      />


      {/* 생산계획 종결 확인 다이얼로그 */}
      <Dialog open={closeConfirmDialog} onClose={() => setCloseConfirmDialog(false)}>
        <DialogTitle>생산계획 종결</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>"{planData?.planNumber}"</strong> 생산계획을 종결하시겠습니가?
            <br /><br />
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2">
                ⚠️ 종결된 생산계획은 수정할 수 없습니다.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                변경이 필요한 경우 새로운 생산계획을 생성해주세요.
              </Typography>
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmDialog(false)}>
            취소
          </Button>
          <Button 
            onClick={confirmClosePlan} 
            variant="contained" 
            color="error"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : '종결'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 생산계획 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirmDialog} onClose={() => setDeleteConfirmDialog(false)}>
        <DialogTitle>생산계획 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>"{planData?.planNumber}"</strong> 생산계획을 삭제하시겠습니까?
            <br /><br />
            <Alert severity="error" sx={{ mt: 1 }}>
              <Typography variant="body2">
                ⚠️ 삭제된 생산계획은 복구할 수 없습니다.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                관련된 설계, 원자재, 생산, QC 데이터도 함께 삭제될 수 있습니다.
              </Typography>
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(false)}>
            취소
          </Button>
          <Button 
            onClick={confirmDeletePlan} 
            variant="contained" 
            color="error"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 저장 중 오버레이 */}
      {saving && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box sx={{ textAlign: 'center', bgcolor: 'white', p: 3, borderRadius: 2 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>처리 중...</Typography>
          </Box>
        </Box>
      )}

      {/* 에러 Snackbar - 화면 상단 중앙에 표시 */}
      <Snackbar
        open={!!error}
        autoHideDuration={10000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert 
          severity="error" 
          variant="filled"
          onClose={() => setError(null)}
          sx={{ 
            width: '100%', 
            minWidth: 400,
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* 성공 Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert 
          severity="success" 
          variant="filled"
          onClose={() => setSuccessMessage(null)}
          sx={{ 
            width: '100%', 
            minWidth: 400,
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductionPlanList;
